import {
  Container,
  Grid,
  Slide,
  Typography,
  Chip,
  CircularProgress,
  Button
} from "@mui/material";
import { Box } from "@mui/system";
import fromApi from "actions/fromApi";
import PageScaffold from "components/PageScaffold";
import timasColor from "components/timasColor";
import TimasHero from "components/TimasHero";
import { prism1, prism2, projectsPageBackground } from "components/timasImages";
import TimasSpinner from "components/TimasSpinner";
import TransitionWhenVisible from "components/TransitionWhenVisible";
import useFromApi from "hooks/useFromApi";
import useResourceMapper from "hooks/useResourceMapper";
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from "react-router-dom";
import { EffectFade, Navigation, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";
import Markdown from "markdown-to-jsx";
import { useSelector } from "react-redux";
import { formatDateOnly } from "utils/formatDateOnly";

function CSRDetails() {
  const { csrId } = useParams();
  const nav = useNavigate();

  const csrApi = useFromApi(fromApi.getCSR(csrId), [csrId], () => csrId);
  const csr = useResourceMapper("csrs", csrApi.sortOrder)?.[0];

  const allCsrs = useSelector(state => state.csrs);
  const nextCsr = csr?.nextCsrId ? allCsrs?.[csr.nextCsrId] : null;

  console.log(allCsrs);
  const formatDate = dateString =>
    formatDateOnly(dateString, "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });

  return (
    <PageScaffold>
      <Helmet>
        <title>{csr ? csr.title : "CSR Details"} - Timas</title>
      </Helmet>
      {!csrApi.loading ? (
        csr ? (
          <>
            <Helmet>
              <meta
                property="og:image"
                content={
                  csr.thumbnailUrl ||
                  (csr.images?.length
                    ? csr.images[0].url
                    : projectsPageBackground)
                }
              />
              <meta property="og:title" content={csr.title} />
              <meta name="description" content={csr.description} />
              <meta name="og:description" content={csr.description} />
            </Helmet>
            <TimasHero
              backgroundUrl={
                csr.bannerImage ||
                csr.thumbnailUrl ||
                (csr.images?.length
                  ? csr.images[0].url
                  : projectsPageBackground)
              }
              backgroundSx={{
                backgroundSize: "fit",
                backgroundPosition: "center"
              }}
            >
              <Grid container spacing={{ xs: 2, sm: 3, md: 5 }}>
                <Grid item xs={12} sm={12} md={8}>
                  <Slide in direction="right">
                    <div>
                      <Typography
                        variant="overline"
                        color={timasColor.white}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: { xs: 1, sm: 2 },
                          paddingBottom: { xs: "8px", sm: "12px", md: "16px" },
                          fontSize: { xs: "0.65rem", sm: "0.75rem" }
                        }}
                      >
                        Corporate Social Responsibility
                        {csr.isPublished && (
                          <Chip
                            label="Published"
                            size="small"
                            sx={{
                              backgroundColor: timasColor.secondary,
                              color: timasColor.white,
                              fontSize: { xs: "0.6rem", sm: "0.7rem" }
                            }}
                          />
                        )}
                      </Typography>
                      <Typography
                        variant="h3"
                        component="h1"
                        color={timasColor.white}
                        sx={{
                          fontWeight: "bold",
                          mb: { xs: 1, sm: 1.5, md: 2 },
                          fontSize: { xs: "1.75rem", sm: "2rem", md: "3rem" },
                          lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 }
                        }}
                      >
                        {csr.title}
                      </Typography>
                      <Typography
                        variant="h6"
                        component="p"
                        color={timasColor.white}
                        sx={{
                          opacity: 0.9,
                          fontSize: {
                            xs: "0.875rem",
                            sm: "1rem",
                            md: "1.25rem"
                          },
                          lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 }
                        }}
                      >
                        {csr.description}
                      </Typography>
                    </div>
                  </Slide>
                </Grid>
                {(csr.eventDate || csr.location) && (
                  <Grid item xs={12} sm={12} md={4}>
                    <Slide in direction="left">
                      <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {csr.eventDate && (
                          <Grid item xs={12} sm={6} md={12}>
                            <Typography
                              variant="overline"
                              color={timasColor.white1}
                              marginBottom={0.5}
                              sx={{
                                fontSize: { xs: "0.65rem", sm: "0.75rem" }
                              }}
                            >
                              Event Date
                            </Typography>
                            <Typography
                              variant="h6"
                              color={timasColor.white}
                              sx={{
                                fontSize: {
                                  xs: "0.875rem",
                                  sm: "1rem",
                                  md: "1.25rem"
                                }
                              }}
                            >
                              {formatDate(csr.eventDate)}
                            </Typography>
                          </Grid>
                        )}
                        {csr.location && (
                          <Grid item xs={12} sm={6} md={12}>
                            <Typography
                              variant="overline"
                              color={timasColor.white1}
                              marginBottom={0.5}
                              sx={{
                                fontSize: { xs: "0.65rem", sm: "0.75rem" }
                              }}
                            >
                              Location
                            </Typography>
                            <Typography
                              variant="h6"
                              color={timasColor.white}
                              sx={{
                                fontSize: {
                                  xs: "0.875rem",
                                  sm: "1rem",
                                  md: "1.25rem"
                                }
                              }}
                            >
                              {csr.location}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Slide>
                  </Grid>
                )}
              </Grid>
            </TimasHero>

            <article>
              {/* Highlights Section */}
              {csr.highlights && (
                <TransitionWhenVisible>
                  <Box
                    sx={{
                      mt: { xs: 3, sm: 4, md: 5 },
                      py: { xs: 4, sm: 6, md: 8 },
                      backgroundColor: timasColor.lightGray,
                      width: "100%"
                    }}
                  >
                    <Container
                      sx={{
                        px: { xs: 2, sm: 3, md: 4 },
                        pr: { xs: 2, sm: 3, md: 4 },
                        overflow: "visible"
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          gap: { xs: 2, sm: 2.5, md: 3 },
                          alignItems: "stretch",
                          width: "100%"
                        }}
                      >
                        {csr.highlightImage && (
                          <Box
                            sx={{
                              flex: { xs: "0 0 auto", md: "0 0 65%" },
                              display: "flex",
                              width: { xs: "100%", md: "auto" },
                              minHeight: {
                                xs: "250px",
                                sm: "300px",
                                md: "auto"
                              }
                            }}
                          >
                            <img
                              src={csr.highlightImage}
                              alt="Highlights"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                              }}
                            />
                          </Box>
                        )}
                        <Box
                          sx={{
                            flex: csr.highlightImage
                              ? { xs: "0 0 auto", md: "0 0 calc(35% - 24px)" }
                              : "1",
                            width: { xs: "100%", md: "auto" },
                            py: { xs: 4, sm: 5, md: 6 },
                            px: { xs: 2, sm: 3, md: 4 },
                            mx: { xs: 0, sm: 0, md: 0 },
                            mr: { xs: 0, sm: 0, md: 0 },
                            background: `url(${prism1}) -40px bottom, url(${prism2}) right -60px top, ${timasColor.primary.gradient()}`,
                            backgroundSize: {
                              xs: "60%, 40%, 100%",
                              sm: "65%, 45%, 100%",
                              md: "70%, 50%, 100%"
                            },
                            backgroundRepeat: "no-repeat",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: {
                              xs: "250px",
                              sm: "300px",
                              md: "400px"
                            },
                            textAlign: "center",
                            borderRadius: "8px",
                            boxSizing: "border-box"
                          }}
                        >
                          {csrApi.loading ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                color: timasColor.white,
                                height: "100%"
                              }}
                            >
                              <CircularProgress color="inherit" />
                            </Box>
                          ) : (
                            <Box>
                              <Typography
                                variant="h5"
                                component="h5"
                                sx={{
                                  color: timasColor.white,
                                  fontWeight: "bold",
                                  lineHeight: { xs: 1.3, sm: 1.35, md: 1.4 },
                                  textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                                  textAlign: { xs: "center", md: "left" },
                                  fontSize: {
                                    xs: "1rem",
                                    sm: "1.25rem",
                                    md: "1.5rem"
                                  },
                                  px: { xs: 1, md: 0 }
                                }}
                              >
                                {csr.highlights}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Container>
                  </Box>
                </TransitionWhenVisible>
              )}

              {/* Body Content Section */}
              {csr.body && (
                <TransitionWhenVisible>
                  <Container
                    sx={{
                      pb: { xs: 6, sm: 8, md: 12 },
                      pt: { xs: 4, sm: 5, md: 6 },
                      px: { xs: 2, sm: 3, md: 4 }
                    }}
                  >
                    <Grid
                      container
                      spacing={{ xs: 3, sm: 4, md: 6 }}
                      alignItems="flex-start"
                    >
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            position: "relative",
                            mb: { xs: 2, sm: 3, md: 4 }
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              width: "4px",
                              height: "100%",
                              backgroundColor: timasColor.primary.main
                            }}
                          />
                          <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                              fontWeight: "medium",
                              pl: { xs: 2, sm: 2.5, md: 3 },
                              color: timasColor.primary.main,
                              fontSize: {
                                xs: "1.5rem",
                                sm: "2rem",
                                md: "3rem"
                              }
                            }}
                          >
                            Highlights
                          </Typography>
                        </Box>
                        <Box sx={{ pl: { xs: 2, sm: 2.5, md: 3 } }}>
                          <Markdown
                            options={{
                              overrides: {
                                h1: props => (
                                  <Typography
                                    variant="h4"
                                    component="h3"
                                    sx={{
                                      color: timasColor.primary,
                                      mt: 3,
                                      mb: 2
                                    }}
                                  >
                                    {props.children}
                                  </Typography>
                                ),
                                h2: props => (
                                  <Typography
                                    variant="h5"
                                    component="h4"
                                    sx={{
                                      color: timasColor.primary,
                                      mt: 3,
                                      mb: 2
                                    }}
                                  >
                                    {props.children}
                                  </Typography>
                                ),
                                h3: props => (
                                  <Typography
                                    variant="h6"
                                    component="h5"
                                    sx={{
                                      color: timasColor.primary,
                                      mt: 3,
                                      mb: 2
                                    }}
                                  >
                                    {props.children}
                                  </Typography>
                                ),
                                p: props => (
                                  <Typography
                                    component="p"
                                    sx={{
                                      mb: 2,
                                      lineHeight: 1.8,
                                      fontSize: "1rem",
                                      color: timasColor.black,
                                      textAlign: "justify"
                                    }}
                                  >
                                    {props.children}
                                  </Typography>
                                ),
                                ul: props => (
                                  <Typography
                                    component="ul"
                                    sx={{ pl: 3, mb: 2 }}
                                  >
                                    {props.children}
                                  </Typography>
                                ),
                                ol: props => (
                                  <Typography
                                    component="ol"
                                    sx={{ pl: 3, mb: 2 }}
                                  >
                                    {props.children}
                                  </Typography>
                                ),
                                li: props => (
                                  <Typography
                                    component="li"
                                    sx={{
                                      mb: 1,
                                      lineHeight: 1.8
                                    }}
                                  >
                                    {props.children}
                                  </Typography>
                                )
                              }
                            }}
                          >
                            {csr.body}
                          </Markdown>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            paddingBottom: {
                              xs: "40px",
                              sm: "45px",
                              md: "50px"
                            }
                          }}
                        >
                          <Swiper
                            pagination={{
                              clickable: true
                            }}
                            navigation={{
                              enabled: true,
                              hideOnClick: false
                            }}
                            effect="fade"
                            slidesPerView="auto"
                            centeredSlides={true}
                            spaceBetween={30}
                            modules={[Pagination, Navigation, EffectFade]}
                            breakpoints={{
                              320: {
                                navigation: {
                                  enabled: false
                                },
                                spaceBetween: 15
                              },
                              600: {
                                navigation: {
                                  enabled: false
                                },
                                spaceBetween: 20
                              },
                              768: {
                                navigation: {
                                  enabled: true
                                },
                                spaceBetween: 30
                              }
                            }}
                          >
                            {csr.images.map((img, idx) => (
                              <SwiperSlide key={idx}>
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: {
                                      xs: "250px",
                                      sm: "350px",
                                      md: "500px"
                                    },
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                  }}
                                >
                                  <img
                                    src={img.url}
                                    alt={`${csr.title} - ${idx + 1}`}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: "8px"
                                    }}
                                  />
                                </Box>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        </Box>
                      </Grid>
                    </Grid>
                  </Container>
                </TransitionWhenVisible>
              )}

              {/* Next CSR Section */}
              {nextCsr && (
                <TransitionWhenVisible>
                  <Box
                    sx={{
                      position: "relative",
                      height: { xs: "350px", sm: "400px", md: "500px" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      background: nextCsr.bannerImage
                        ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${nextCsr.bannerImage})`
                        : nextCsr.thumbnailUrl
                        ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${nextCsr.thumbnailUrl})`
                        : timasColor.primary.gradient(),
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      cursor: "pointer"
                    }}
                    onClick={() => {
                      window.scrollTo(0, 0);
                      nav(`/csr/${nextCsr.id}`);
                    }}
                  >
                    <Container sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
                      <Box
                        sx={{
                          maxWidth: { xs: "100%", sm: "500px", md: "600px" }
                        }}
                      >
                        <Typography
                          variant="overline"
                          sx={{
                            color: timasColor.white,
                            display: "block",
                            mb: { xs: 0.5, sm: 0.75, md: 1 },
                            letterSpacing: "0.1em",
                            fontSize: {
                              xs: "0.65rem",
                              sm: "0.75rem",
                              md: "0.875rem"
                            }
                          }}
                        >
                          Next CSR
                        </Typography>
                        <Typography
                          variant="h4"
                          component="h4"
                          sx={{
                            color: timasColor.white,
                            fontWeight: "bold",
                            mb: { xs: 2, sm: 2.5, md: 3 },
                            textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
                            fontSize: {
                              xs: "1.25rem",
                              sm: "1.75rem",
                              md: "2.125rem"
                            },
                            lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 }
                          }}
                        >
                          {nextCsr.title}
                        </Typography>
                        <Button
                          variant="contained"
                          sx={{
                            backgroundColor: "#4CAF50",
                            color: timasColor.white,
                            px: { xs: 3, sm: 3.5, md: 4 },
                            py: { xs: 1, sm: 1.25, md: 1.5 },
                            fontSize: {
                              xs: "0.875rem",
                              sm: "0.9375rem",
                              md: "1rem"
                            },
                            fontWeight: "bold",
                            "&:hover": {
                              backgroundColor: "#45a049"
                            }
                          }}
                        >
                          See More &gt;&gt;
                        </Button>
                      </Box>
                    </Container>
                  </Box>
                </TransitionWhenVisible>
              )}
            </article>
          </>
        ) : (
          <TimasHero
            title="CSR Not Found"
            description="The CSR program you're looking for could not be found."
          />
        )
      ) : (
        <TimasSpinner />
      )}
    </PageScaffold>
  );
}

export default CSRDetails;
